import { SellingMode } from "../enums/sellingMode";
import { Unit } from "../enums/unit";

/**
 * Units that are valid for MEASURED selling mode.
 * Per PRD: unit must NOT be UNIT for MEASURED products.
 */
export const MEASURED_UNITS: Unit[] = [
  Unit.G,
  Unit.KG,
  Unit.ML,
  Unit.L,
  Unit.CM,
  Unit.M,
  Unit.MM,
  Unit.SQCM,
  Unit.OZ,
  Unit.LB,
  Unit.BOTTLE
];

/**
 * Maps each selling mode to its allowed base units.
 *
 * SINGLE_ITEM → only UNIT (qty is always 1, sold as whole item)
 * UNIT_BASED  → only UNIT (countable units from a container)
 * MEASURED    → all units except UNIT (fractional/partial sales by measurement)
 */
const ALLOWED_UNITS_BY_SELLING_MODE: Record<SellingMode, Unit[]> = {
  [SellingMode.SINGLE_ITEM]: [Unit.UNIT],
  [SellingMode.UNIT_BASED]: [Unit.UNIT],
  [SellingMode.MEASURED]: MEASURED_UNITS
};

/**
 * Returns the allowed base units for a given selling mode.
 * Falls back to all units when selling mode is undefined.
 */
export const getAllowedUnitsForSellingMode = (
  sellingMode: SellingMode | undefined
): Unit[] => {
  if (!sellingMode) return Object.values(Unit);
  return ALLOWED_UNITS_BY_SELLING_MODE[sellingMode] ?? Object.values(Unit);
};

/**
 * Maps each selling mode to whether its base unit is locked (forced to UNIT).
 * When locked, the baseUnit field should be disabled in the UI.
 */
const BASE_UNIT_LOCKED_BY_SELLING_MODE: Record<SellingMode, boolean> = {
  [SellingMode.SINGLE_ITEM]: true,
  [SellingMode.UNIT_BASED]: true,
  [SellingMode.MEASURED]: false
};

/**
 * Returns true if the selling mode forces a single fixed unit (UNIT),
 * meaning the baseUnit field should be locked/disabled.
 */
export const isBaseUnitLocked = (
  sellingMode: SellingMode | undefined
): boolean => {
  if (!sellingMode) return false;
  return BASE_UNIT_LOCKED_BY_SELLING_MODE[sellingMode] ?? false;
};

/**
 * Maps each selling mode to the unit that should be auto-selected.
 * Undefined means no auto-selection applies (user must pick).
 */
const AUTO_SELECTED_UNIT_BY_SELLING_MODE: Record<
  SellingMode,
  Unit | undefined
> = {
  [SellingMode.SINGLE_ITEM]: Unit.UNIT,
  [SellingMode.UNIT_BASED]: Unit.UNIT,
  [SellingMode.MEASURED]: undefined
};

/**
 * Returns the unit that should be auto-selected when the selling mode
 * forces a fixed unit. Returns undefined if no auto-selection applies.
 */
export const getAutoSelectedUnit = (
  sellingMode: SellingMode | undefined
): Unit | undefined => {
  if (!sellingMode) return undefined;
  return AUTO_SELECTED_UNIT_BY_SELLING_MODE[sellingMode];
};

/**
 * Maps each selling mode to whether the user may set isPartialSellingAllowed.
 *   SINGLE_ITEM → always one whole item; partial selling not configurable.
 *   UNIT_BASED  → integer unit quantities; partial selling (sell less than full stock) is configurable.
 *   MEASURED    → fractional quantities; partial selling is configurable.
 */
const PARTIAL_SELLING_ALLOWED_BY_SELLING_MODE: Record<SellingMode, boolean> = {
  [SellingMode.SINGLE_ITEM]: false,
  [SellingMode.UNIT_BASED]: true,
  [SellingMode.MEASURED]: true
};

/**
 * Returns true if partial selling is permitted for the given selling mode.
 * When false, the isPartialSellingAllowed field should be locked/disabled.
 */
export const isPartialSellingPermitted = (
  sellingMode: SellingMode | undefined
): boolean => {
  if (!sellingMode) return false;
  return PARTIAL_SELLING_ALLOWED_BY_SELLING_MODE[sellingMode] ?? false;
};
